import React from 'react';
import {List} from 'immutable';
import './ReviewFileUploads.styl';
import {ReviewFileUpload} from 'components/coaching/ReviewFileUpload';
import range from 'lodash/range';
import Button from '@material-ui/core/Button';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import throttle from 'lodash/throttle';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import {navigateToCallsTab} from 'utils/navigateToCallsTab';
import AuthStore from 'stores/AuthStore';
import LiveCoachingActions from 'actions/scorecards/LiveCoachingActions';
import {browserHistory} from 'react-router';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import CallLink, {navToLiveCoachingCall} from 'components/coaching/CallLink';
import partitionBy from 'utils/partitionBy';
import GenericLink from 'components/GenericLink';

async function startLiveCoaching(plan, review) {
  const coach = AuthStore.getState().user;
  const rep = plan.recipient.info;

  // create the call
  const call = await LiveCoachingActions.createEmpty([rep.id, coach.id]);

  const onSubmit = () => {
    // decrement the number of stubs when finished
    CoachingReviewActions.setLiveCoachingStubs(review.id, review.livecoachingStubs - 1);
  };

  browserHistory.push({
    pathname: `/live-coaching`,
    state: {
      callId: call.id,
      callerId: rep.id,
      coachId: coach.id,
      planId: plan.id,
      back: window.location.pathname + window.location.search,
      onSubmit,
      nonAdHoc: true,
      reviewId: review.id,
    },
  });
}

export default class ReviewFileUploads extends React.PureComponent {
  constructor(props) {
    super(props);
    this.addCallsInstead = throttle(this.addCallsInstead.bind(this), 5000);
    this.removeStub = throttle(this.removeStub.bind(this), 1000);
  }

  // Decrement the number of stubs, and move to the calls tab with playlist open
  addCallsInstead() {
    const {plan} = this.props;
    this.removeStub().then(() => {
      CoachingPlaylistActions.setAddCallsMode({
        planId: plan.id,
        recipient: plan.recipient.info,
      });
      navigateToCallsTab();
    });
  }

  removeStub() {
    const {review} = this.props;
    return CoachingReviewActions.setLiveCoachingStubs(review.id, review.livecoachingStubs - 1);
  }

  render() {
    const {
      reviewFileUploads = List(),
      calls,
      users,
      plan,
      disabled,
      otherUser,
      review,
      isCoach,
      editFeedback,
    } = this.props;
    const showEmptyState =
      reviewFileUploads && reviewFileUploads.size === 0 && review.livecoachingStubs === 0;

    // we need the original index to send back for the callback to update the review fileupload.
    // but we hide the livecoaching review fileupload.
    // so we capture the indexes here before iterating over them
    const [livecoachingCompletedCallsWithIndex, regularCallsWithIndex] = partitionBy(
      reviewFileUploads.map((rfu, i) => [i, rfu]),
      ([, rfu]) => rfu.livecoaching
    );

    return (
      <div className="review-fileuploads">
        <h5>Calls for this meeting</h5>
        {showEmptyState ? (
          <p className="no-calls">There are no calls that have been added for this meeting.</p>
        ) : (
          [
            ...range(review.livecoachingStubs).map(i => (
              <section key={i} className="call-for-review">
                <div className="call-details livecoaching">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isCoach || disabled}
                    onClick={throttle(() => startLiveCoaching(plan, review), 5000)}>
                    Live Coach Now
                  </Button>
                  {isCoach && disabled && (
                    <div className="add-call-instead">
                      <span className="generic-link pointer" onClick={this.addCallsInstead}>
                        Add call instead
                      </span>{' '}
                      |{' '}
                      <span className="generic-link pointer" onClick={this.removeStub}>
                        Remove
                      </span>
                    </div>
                  )}
                </div>
                <div className="livecoaching-feedback">
                  <div>
                    Once the meeting starts, click the button to the left to begin live coaching a
                    call. This call will be recorded and contribute to the overall meeting feedback.
                  </div>
                </div>
              </section>
            )),
            regularCallsWithIndex.map(([i, reviewFileUpload]) => (
              <ReviewFileUpload
                key={reviewFileUpload.id}
                reviewFileUpload={reviewFileUpload}
                call={calls.get(reviewFileUpload.fileuploadId)}
                addedBy={reviewFileUpload.addedByUserId.map(u => users.get(u))}
                disabled={disabled}
                plan={plan}
                otherUser={otherUser}
                setFileUploadScore={(val, feedbackIndex) =>
                  this.props.setFileUploadScore(val, i, feedbackIndex)
                }
                isCoach={isCoach}
                review={review}
              />
            )),
            livecoachingCompletedCallsWithIndex.map(([i, lcc]) => (
              <LivecoachingCompletedStub
                reviewFileUpload={lcc}
                call={calls.get(lcc.fileuploadId)}
                addedBy={users.get(plan.coach.id)}
                editing={editFeedback}
                reviewId={review.id}
              />
            )),
          ]
        )}
      </div>
    );
  }
}

const LivecoachingCompletedStub = ({reviewFileUpload, call, addedBy, editing, reviewId}) => {
  return (
    <section className="call-for-review">
      <div className="call-details">
        <div className="description">
          <CallLink call={call} reviewFileUpload={reviewFileUpload} />
          <div>
            Added for review by {addedBy && addedBy.getDisplayName()} on{' '}
            {reviewFileUpload.addedOn.format('MM/DD/YYYY')}
          </div>
        </div>
      </div>
      <div className="livecoaching-feedback">
        <div>
          {!editing ? (
            <div className="finish-wrapper">
              <div>
                <CheckIcon height={30} className="check" />
              </div>
              <div>
                You've completed live coaching associated with this meeting. Scores will be shown
                when the rest of the meeting is completed.
              </div>
            </div>
          ) : (
            <GenericLink onClick={() => navToLiveCoachingCall(call, reviewId)}>
              Edit live coaching scorecards
            </GenericLink>
          )}
        </div>
      </div>
    </section>
  );
};
